/**
 * Partner Dashboard - Dashboard para PAT/Prefeituras
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { partnersAPI, candidatesAPI, cartasAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Landmark,
  Users,
  FileText,
  Settings,
  Loader2,
  Search,
  LogOut,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  Upload,
  Briefcase,
  ListChecks,
  BarChart3,
  QrCode
} from 'lucide-react';
import { PartnerCandidatesTab } from '../../components/partner/PartnerCandidatesTab';
import { PartnerJobsTab } from '../../components/partner/PartnerJobsTab';
import { PartnerReferralsTab } from '../../components/partner/PartnerReferralsTab';
import { PartnerReportTab } from '../../components/partner/PartnerReportTab';
import { PartnerInviteTab } from '../../components/partner/PartnerInviteTab';

const statusColors = {
  contratado: 'bg-green-100 text-green-700',
  aprovado_aguardando: 'bg-blue-100 text-blue-700',
  nao_contratado: 'bg-red-100 text-red-700',
  nao_compareceu: 'bg-slate-100 text-slate-700',
  pendente: 'bg-amber-100 text-amber-700'
};

const statusLabels = {
  contratado: 'Contratado',
  aprovado_aguardando: 'Aprovado (Aguardando)',
  nao_contratado: 'Não Contratado',
  nao_compareceu: 'Não Compareceu',
  pendente: 'Aguardando Retorno'
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [cartas, setCartas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('meus-candidatos');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get partner profile
      const myPartner = (await partnersAPI.getMe().catch(() => null))?.data;
      
      if (myPartner) {
        setPartner(myPartner);
        
        // Get candidates from this partner
        // Note: This would need a backend endpoint to filter by partner_id
        // For now, we'll use the cartas endpoint
        const cartasRes = await cartasAPI.getAll();
        setCartas(cartasRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !partner) return;
    
    try {
      await partnersAPI.uploadLogo(partner.id, file);
      toast.success('Logo atualizada com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar logo');
    }
  };

  const filteredCartas = cartas.filter(c => 
    c.candidato_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.vaga_titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.empresa_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    totalCandidatos: partner?.total_candidatos || 0,
    totalCartas: cartas.length,
    contratados: cartas.filter(c => c.status_retorno === 'contratado').length,
    aguardando: cartas.filter(c => !c.status_retorno).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Perfil não encontrado</h2>
            <p className="text-slate-600 mb-4">
              Complete seu cadastro como parceiro para acessar o dashboard.
            </p>
            <Button onClick={() => navigate('/partner/register')}>
              Completar Cadastro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {partner.logo_url ? (
                <img 
                  src={partner.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}` : partner.logo_url} 
                  alt={partner.nome}
                  className="w-10 h-10 rounded-lg object-contain bg-white"
                />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Landmark className="w-5 h-5" />
                </div>
              )}
              <div>
                <h1 className="font-bold">{partner.nome}</h1>
                <p className="text-xs text-white/80">{partner.municipio}/{partner.estado}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalCandidatos}</p>
                  <p className="text-sm text-slate-500">Candidatos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalCartas}</p>
                  <p className="text-sm text-slate-500">Encaminhamentos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.contratados}</p>
                  <p className="text-sm text-slate-500">Contratados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.aguardando}</p>
                  <p className="text-sm text-slate-500">Aguardando</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="meus-candidatos" className="gap-2" data-testid="partner-tab-candidates">
              <Users className="w-4 h-4" />
              Meus Candidatos
            </TabsTrigger>
            <TabsTrigger value="vagas" className="gap-2" data-testid="partner-tab-jobs">
              <Briefcase className="w-4 h-4" />
              Indicar para Vagas
            </TabsTrigger>
            <TabsTrigger value="acompanhamento" className="gap-2" data-testid="partner-tab-referrals">
              <ListChecks className="w-4 h-4" />
              Acompanhamento
            </TabsTrigger>
            <TabsTrigger value="candidatos" className="gap-2">
              <FileText className="w-4 h-4" />
              Cartas
            </TabsTrigger>
            <TabsTrigger value="relatorio" className="gap-2" data-testid="partner-tab-report">
              <BarChart3 className="w-4 h-4" />
              Relatório
            </TabsTrigger>
            <TabsTrigger value="convite" className="gap-2" data-testid="partner-tab-invite">
              <QrCode className="w-4 h-4" />
              Convite
            </TabsTrigger>
            <TabsTrigger value="configuracoes" className="gap-2">
              <Settings className="w-4 h-4" />
              Configurações
            </TabsTrigger>
          </TabsList>

          <TabsContent value="meus-candidatos"><PartnerCandidatesTab onChanged={() => partnersAPI.getMe().then((r) => setPartner(r.data)).catch(() => {})} /></TabsContent>
          <TabsContent value="vagas"><PartnerJobsTab /></TabsContent>
          <TabsContent value="acompanhamento"><PartnerReferralsTab /></TabsContent>
          <TabsContent value="relatorio"><PartnerReportTab /></TabsContent>
          <TabsContent value="convite"><PartnerInviteTab partner={partner} /></TabsContent>

          {/* Encaminhamentos Tab */}
          <TabsContent value="candidatos">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Cartas de Encaminhamento</CardTitle>
                    <CardDescription>
                      Acompanhe os candidatos encaminhados para empresas
                    </CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar..."
                      className="pl-10 w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCartas.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Nenhum encaminhamento encontrado</p>
                    <p className="text-sm mt-2">
                      Os encaminhamentos aparecerão aqui quando empresas selecionarem seus candidatos.
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidato</TableHead>
                        <TableHead>Vaga</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCartas.map((carta) => (
                        <TableRow key={carta.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium">{carta.candidato_nome}</p>
                                <p className="text-xs text-slate-500">{carta.candidato_cpf}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{carta.vaga_titulo}</TableCell>
                          <TableCell>{carta.empresa_nome}</TableCell>
                          <TableCell>
                            <p className="text-sm">{carta.data_entrevista}</p>
                            <p className="text-xs text-slate-500">{carta.hora_entrevista}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[carta.status_retorno || 'pendente']}>
                              {statusLabels[carta.status_retorno || 'pendente']}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configurações Tab */}
          <TabsContent value="configuracoes">
            <Card>
              <CardHeader>
                <CardTitle>Configurações da Entidade</CardTitle>
                <CardDescription>Gerencie as informações do PAT/Prefeitura</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}` : partner.logo_url} 
                        alt={partner.nome}
                        className="w-24 h-24 rounded-xl object-contain bg-slate-100 border"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center border">
                        <Landmark className="w-10 h-10 text-slate-400" />
                      </div>
                    )}
                    <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-amber-600 transition-colors">
                      <Upload className="w-4 h-4 text-white" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{partner.nome}</h3>
                    <p className="text-slate-500">{partner.tipo}</p>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">CNPJ</p>
                      <p className="font-medium">{partner.cnpj || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Localização</p>
                      <p className="font-medium">{partner.municipio}/{partner.estado}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Telefone</p>
                      <p className="font-medium">{partner.telefone || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{partner.email_contato || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Responsável</p>
                      <p className="font-medium">{partner.responsavel || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Endereço</p>
                      <p className="font-medium">{partner.endereco || 'Não informado'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
